import { Card, CardContent } from "@/components/ui/Card";
import { LiveQueue, TicketStatus, useGetAllTicketsQuery } from "@/services/LiveQueueService";
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWebSocket } from "@/constants/websocket/Websocket";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { Department } from "@/app/types/counter";
import { Button } from "@/components/ui/Button";
export const transformLiveQueueToDepartment = (
  liveQueues: LiveQueue[]
): LiveQueue[] => {
  return liveQueues.map((queue) => ({
    id: queue.id.toString(),
    name: queue.name,
    status: queue.status, // Add the missing status property
    counters: queue.counters.map((counter, index) => ({
      id: counter.id,
      number: parseInt(counter.id) + index + 100, // Example logic for generating a unique number
      name: `Counter ${index + 1}`, // Assigns sequential names
      service: counter.service, // Include the service property
      counter_code: counter.counter_code,
      tickets: counter.tickets.map((ticket) => ({
        title:ticket.title,
        description:ticket.description,
        time:ticket.time,
        date:ticket.date,
        ticket_id: ticket.id,
        id: ticket.number,
        r_ticket_number:ticket.r_ticket_number,
        ticket_number: ticket.ticket_number,
        number: ticket.number,
        status: ticket.status,
        reserved_number: ticket.reserved_number,
        created_at: ticket.created_at,
        served_at: ticket.served_at,
        eta: ticket.eta,
        feedback: ticket.feedback,
        user_id: ticket.user_id,
        served_by: ticket.served_by,
        user_details: ticket.user_details,
        comments: ticket.comments,
        cancel_reason: ticket.cancel_reason,
      })),
    })),
  }));
};
const { VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL } = import.meta.env;

export default function DepartmentGrid() {
  useDocumentTitle("Queue Monitor");
  const today = new Date();
  const { ws, setRefetch } = useWebSocket();
  const localDate = today.toLocaleDateString("en-CA"); //
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = useSelector((state: RootState) => state.sidePanel);

  const isPublic = location.pathname == "/live-queue";

  const queryParams = new URLSearchParams(location.search);
  const department_id = queryParams.get("department_id");

  const { data, isLoading, error, refetch } = useGetAllTicketsQuery({
    query: `${VITE_IS_LOCAL === "true"
      ? VITE_LOCAL_URL
      : VITE_IS_PRODUCTION === "true"
        ? window.location.hostname
        : window.location.host}&date_filter=${localDate}${department_id ? "&department_id="+department_id : ""}`,
  });
  // Extract page query parameter
  const startParam = queryParams.get("start");
  const endParam = queryParams.get("end");

  // Convert params to numbers
  const startIndex = startParam ? parseInt(startParam, 10) : 0;
  const endIndex = endParam ? parseInt(endParam, 10) : data?.length || 0;
  // const endIndex = endParam ? parseInt(endParam, 10) : dummyData.length;

  useEffect(() => {
    if (ws) {
      // Set up a message handler for the WebSocket
      ws.onmessage = (event) => {
        console.log(event)
        try {
          if (!event.data) {
            return;
          }
          let data;
          if (typeof event.data === 'string') {
            try {
              data = JSON.parse(event.data);
            } catch (jsonError) {
              try {
                const fixedJson = event.data
                  .replace(/(\w+):/g, '"$1":')  // Add quotes around property names
                  .replace(/'/g, '"');          // Replace single quotes with double quotes
                  
                data = JSON.parse(fixedJson);
              } catch (fixError) {
                try {
                  const jsObjStr = event.data.trim();
                  data = Function('return ' + jsObjStr)();
                } catch (evalError) {
                  return;
                }
              }
            }
          } else {
            return;
          }
          
          // Now that we have parsed data (hopefully), check the org_id
          const messageOrgId = data?.org_id;
          const userOrgId = userInfo?.org_id;
          // Only refetch if the organization IDs match
          if (messageOrgId !== undefined && userOrgId !== undefined && 
              String(messageOrgId) === String(userOrgId)) {
            console.log("Organization ID match found, triggering refetch");
            console.log("userOrgId", userOrgId);
            console.log("messageOrgId", messageOrgId);
            console.log(event.data);
            refetch();
          } else {
            console.log("Organization ID mismatch or missing, skipping refetch");
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error);
        }
      };
    }
  
    // Clean up the event listener when component unmounts
    return () => {
      if (ws) {
        ws.onmessage = null;
      }
    };
  }, [ws, userInfo, refetch]);

  if (isLoading)
    return (
      <div>
        <div className="w-full  mx-auto p-2">
          <h1
            className="text-base font-semibold mb-6 w-1/3 h-6 rounded"
            aria-hidden="true"
          >
            Loading...
          </h1>
          <div className="grid grid-cols-5 gap-2">
            {/* Skeleton for columns */}
            {Array.from({ length: 10 }).map((_, colIndex) => (
              <div key={`skeleton-col-${colIndex}`} className="space-y-2">
                {/* Skeleton for column header */}
                <div className="bg-gray-300 h-8 rounded"></div>
                {/* Skeleton for tickets */}
                <div className="space-y-2 p-2 max-h-[290px] overflow-hidden">
                  {Array.from({ length: 5 }).map((_, rowIndex) => (
                    <div
                      key={`skeleton-ticket-${colIndex}-${rowIndex}`}
                      className="bg-gray-200 h-12 rounded"
                    ></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="p-5">
        <Card className="w-full">
          <div className="flex justify-center items-center">
            <div className="w-full  mx-auto p-4">
              <h1
                className="text-base font-semibold mb-6 rounded"
                aria-hidden="true"
              >
                {"status" in error && error.status === 404 ? (
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <h1 className="text-xl font-semibold">
                      No tickets have been issued yet.
                    </h1>
                    <p>
                      Once tickets are generated for any department, they will
                      appear within the respective department.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-4">
                    ⚠️ No departments or queues found. Please ensure the system
                    is configured and a queue has been started.
                  </div>
                )}
              </h1>
              {/* <div className="grid grid-cols-10 gap-2">
                {Array.from({ length: 10 }).map((_, colIndex) => (
                  <div key={`skeleton-col-${colIndex}`} className="space-y-2">
                    <div className="bg-gray-300 h-8 rounded"></div>
                    <div className="space-y-2  max-h-[290px] overflow-hidden">
                      {Array.from({ length: 5 }).map((_, rowIndex) => (
                        <div
                          key={`skeleton-ticket-${colIndex}-${rowIndex}`}
                          className="bg-gray-200 h-12 rounded"
                        ></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div> */}
            </div>
          </div>
        </Card>
      </div>
    );

  // const departmentData = dummyData
  //   ? transformLiveQueueToDepartment(dummyData)
  //   : [];
  // const currentServices = departmentData?.slice(startIndex, endIndex);
  const departmentData = data ? transformLiveQueueToDepartment(data) : [];
  const currentServices = departmentData?.slice(startIndex, endIndex);

  return (
    <div
      className={`w-full p-5 flex flex-col gap-2 ${isPublic ? "max-h-screen overflow-hidden" : ""}`}
    >
      {!isPublic && <div
        className="bg-white rounded-md p-2"
      >
        <div className="flex justify-start items-center px-2">
          <i className={`fa-solid fa-building text-black`}></i>
          <p className="text-black text-left font-medium p-2">
            Departments
          </p>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {currentServices.map((department: LiveQueue) => (
            <Link key={department.id} to={`/live-queue?department_id=${department.id}`}>
              <Button
                variant={"secondary"}
                className="w-full text-black flex-row hover:bg-lightprimary hover:border-lightprimary  flex justify-start gap-4 gap-y-1 items-start h-fit"
              >
                <span className="text-left text-sm font-medium">
                  {department.name}
                </span>
              </Button>
            </Link>
          ))}
        </div>
      </div>}
      <Card className="bg-gray-50">
        <div className="container mx-auto p-4 w-full">
          <div className="overflow-x-auto w-full">
            <div className="relative h-[500px]">
              {" "}
              {/* Set fixed height */}
              <div className="grid grid-flow-col auto-cols-max gap-6 overflow-x-auto fancy-scroll h-full">
                {currentServices?.map((department: LiveQueue) => (
                  <div
                    key={department.id}
                    className="min-w-[200px] bg-gray-50 flex-1 flex  flex-col h-full justify-center items-center"
                  >
                    <h2 className="text-lg bg-[#B08D57] font-semibold w-full justify-center items-center flex sticky top-0 z-10 p-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {isPublic ? (
                                <p className="text-sm">
                                {department.name?.length > 20
                                  ? `${department.name.slice(0, 17)}...`
                                  : department.name}
                              </p>
                            ) : (
                              <Link to="/live-queue">
                                <p className="text-sm">
                                  {department.name?.length > 20
                                    ? `${department.name.slice(0, 17)}...`
                                    : department.name}
                                </p>
                              </Link>
                            )}
                          </TooltipTrigger>
                          <TooltipContent>{department.name}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </h2>

                    {/* Counters - Scrollable */}
                    <div className="grid grid-flow-col capitalize text-sm font-semibold mdxs:text-lg text-cente auto-cols-max gap-2 overflow-x-auto flex-1">
                      {department.counters.map((counter) => (
                        <div
                          key={counter.id}
                          className="bg-gray-50  py-2 rounded-lg min-w-[200px] max-w-[200px]"
                        >
                          <h3 className="text-lg text-center font-semibold text-black w-full flex justify-center items-center">
                              {`${counter.counter_code} `}
                          </h3>
                          <div className="space-y-2 mt-2">
                           
                            {counter.tickets.map((ticket) => (
                              <TooltipProvider key={ticket.id}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Card
                                      className={`${
                                        ticket.status === TicketStatus.NEW
                                          ? "bg-[#EADDCA]"
                                          : ticket.status ===
                                              TicketStatus.CHECKED_IN
                                            ? "bg-[#D2B48C]"
                                            : ""
                                      } border-none hover:bg-[#FFF9D9] transition-colors cursor-default`}
                                    >
                                      <CardContent className="p-4">
                                        <div className="text-center">
                                          <h3 className="text-sm font-semibold capitalize text-black break-words ">
                                            {counter.service.name}
                                          </h3>
                                          <strong>{ticket.number}</strong>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  </TooltipTrigger>
                                  <TooltipContent className="text-black text-center w-fit">
                                    {ticket.status === TicketStatus.NEW
                                      ? "Pending"
                                      : ticket.status === TicketStatus.CHECKED_IN
                                        ? "Serving"
                                        : "Completed"}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
